import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Globe,
  Search,
  Download,
  Copy,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Monitor,
  Smartphone,
  FileText,
  Mail,
  Loader2,
  ExternalLink,
  RefreshCw,
  History,
  Clock,
  ChevronRight,
  BookOpen,
  X,
  Eye,
  EyeOff
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ========== Components ==========

const AuditHistory = ({ onSelectAudit, onClose }) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/audits`)
      .then((res) => setAudits(res.data))
      .catch(() => toast.error("Failed to load audit history"))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const getDomain = (url) => {
    try { return new URL(url).hostname; }
    catch { return url; }
  };

  const completedAudits = audits.filter(a => a.status === 'completed');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <History className="w-5 h-5" /> Audit History
            </CardTitle>
            <CardDescription>{completedAudits.length} completed audits</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </CardHeader>
        <CardContent className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : completedAudits.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No past audits found</p>
          ) : (
            <div className="space-y-2">
              {completedAudits.map((audit) => (
                <button
                  key={audit.id}
                  onClick={() => onSelectAudit(audit.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                    (audit.score || 0) >= 80 ? 'bg-green-500' :
                    (audit.score || 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'
                  }`}>
                    {audit.grade || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{getDomain(audit.url)}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(audit.created_at)}</span>
                      <span className="text-foreground font-medium">{audit.score}/100</span>
                      {audit.page_count > 1 && (
                        <Badge variant="outline" className="text-xs py-0">
                          {audit.page_count} pages
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SPELLCHECK_ENGINES = [
  { value: 'languagetool', label: 'LanguageTool' },
  { value: 'pyspellchecker', label: 'PySpellChecker' },
  { value: 'hunspell', label: 'Hunspell' },
  { value: 'nspell', label: 'NSpell (Hunspell.js)' },
  { value: 'typojs', label: 'Typo.js' },
];

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

// Short absolute time — "3:23 PM" today, "Apr 24 3:23 PM" otherwise. Used
// in the deploy pill so you always see a concrete time alongside relative.
const shortAbsolute = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (sameDay) return time;
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${date} ${time}`;
};

// Long absolute time — "Fri, Apr 24 at 3:23:45 PM EDT". Used in tooltip.
const longAbsolute = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
};

// Per-service deploy dot. Colored by state, animated while in progress.
// Hover for full tooltip with commit message + timestamps.
const ServiceDot = ({ svc, nameOverride }) => {
  const state = svc.state || "unknown";
  const dotColor =
    state === "in_progress" ? "bg-yellow-500"
      : state === "success" ? "bg-green-500"
      : state === "failed" ? "bg-red-500"
      : "bg-gray-400";

  // Fall back to the first 8 chars of the app UUID when the backend
  // couldn't resolve a name — more useful than "(unknown service)".
  const uuidShort = svc.app_uuid ? svc.app_uuid.slice(0, 8) : "";
  const label = nameOverride || svc.name || uuidShort || "service";
  // Coolify doesn't reliably populate `finished_at` on completed deploys —
  // `updated_at` gets bumped to the completion moment instead, so prefer
  // that as the "when did this finish" source when finished_at is null.
  const completionIso = svc.finished_at || svc.updated_at || null;
  const completionRel = relativeTime(completionIso);
  const completionAbs = shortAbsolute(completionIso);
  const startedAgo = relativeTime(svc.started_at || svc.updated_at);
  const statusText =
    state === "in_progress"
      ? "deploying"
      : state === "success"
      ? (completionAbs ? `deployed ${completionAbs} · ${completionRel}` : "deployed")
      : state === "failed"
      ? (completionAbs ? `failed ${completionAbs} · ${completionRel}` : "failed")
      : startedAgo || "";

  const title = [
    label,
    `status: ${svc.status_raw || state}`,
    svc.commit_message || "",
    svc.short_sha ? `commit ${svc.short_sha}` : "",
    svc.started_at ? `started ${longAbsolute(svc.started_at)}` : "",
    svc.finished_at
      ? `finished ${longAbsolute(svc.finished_at)}`
      : (state === "success" || state === "failed") && svc.updated_at
      ? `finished (approx) ${longAbsolute(svc.updated_at)}`
      : "",
    svc.stale ? "(last known — fresh data unavailable)" : "",
  ].filter(Boolean).join("\n");

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border bg-background text-[11px] font-medium shadow-sm"
      title={title}
      data-testid="service-dot"
      data-service={label}
      data-state={state}
    >
      <span className="relative inline-flex w-2 h-2">
        {state === "in_progress" && (
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${dotColor}`}></span>
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`}></span>
      </span>
      <span className="text-foreground truncate max-w-[110px]">{label}</span>
      {statusText && <span className="text-muted-foreground">· {statusText}</span>}
    </div>
  );
};

// Row of per-service deploy dots. Shows a neutral placeholder when Coolify
// isn't wired up (so the spot never goes completely blank) or when we got
// an empty services list back.
//
// Poll cadence: 5s while any service is in_progress, 30s otherwise.
const DeploymentBadge = () => {
  const [services, setServices] = useState(null); // null = first load
  const [configured, setConfigured] = useState(null);
  const [, setTick] = useState(0); // triggers re-render so "ago" stays fresh

  const anyInProgress = useMemo(
    () => Array.isArray(services) && services.some((s) => s.state === "in_progress"),
    [services],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/deployment`);
        if (cancelled) return;
        if (!r.data) return;
        setConfigured(r.data.configured !== false);
        setServices(Array.isArray(r.data.services) ? r.data.services : []);
      } catch {
        // Likely backend mid-deploy — keep last known state, don't zero out.
      }
    };
    fetchOnce();
    // Backend cache is ~8s while in-progress, 20s otherwise, 120s during a
    // 429 backoff. No benefit to polling faster than the freshest cache.
    const pollMs = anyInProgress ? 8000 : 30000;
    const intervalId = setInterval(fetchOnce, pollMs);
    const timeTick = setInterval(() => setTick((t) => t + 1), 15000);
    return () => { cancelled = true; clearInterval(intervalId); clearInterval(timeTick); };
  }, [anyInProgress]);

  // First-load placeholder — avoids a flash of empty space.
  if (services === null) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-background text-xs text-muted-foreground shadow-sm">
        <span className="inline-block w-2 h-2 rounded-full bg-gray-300 animate-pulse"></span>
        Checking deploys…
      </div>
    );
  }

  // Not wired up to Coolify — show a muted hint instead of nothing.
  if (configured === false || services.length === 0) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-background text-xs text-muted-foreground shadow-sm"
        title="Coolify API env vars are not configured. Set COOLIFY_API_URL, COOLIFY_API_KEY, COOLIFY_APP_UUIDS on the backend and redeploy."
        data-testid="deployment-badge-unconfigured"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
        Deploys: N/A
      </div>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-1.5 flex-wrap"
      data-testid="deployment-badge"
    >
      {services.map((svc, i) => (
        <ServiceDot key={svc.app_uuid || svc.name || i} svc={svc} />
      ))}
    </div>
  );
};

const InputSection = ({ onSubmit, isLoading }) => {
  const [url, setUrl] = useState("");
  const [pageCount, setPageCount] = useState(5);
  const [spellcheckEngine, setSpellcheckEngine] = useState('languagetool');
  const [manualContext, setManualContext] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url.trim(), pageCount, spellcheckEngine, manualContext.trim());
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 pt-24 pb-12" data-testid="input-section">
      <div className="w-full max-w-2xl text-center">
        <div className="mb-8 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary mb-6">
            <Globe className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight text-foreground mb-4">
            Website Audit
          </h1>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            Get a comprehensive SEO, usability, and conversion analysis in seconds
          </p>
        </div>

        <form onSubmit={handleSubmit} className="animate-fade-in-up delay-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Enter website URL (e.g., example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-14 pl-12 text-lg border-2 focus:border-primary input-glow"
                disabled={isLoading}
                data-testid="url-input"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="h-14 px-8 text-lg font-semibold"
              disabled={isLoading || !url.trim()}
              data-testid="generate-audit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Auditing...
                </>
              ) : (
                "Generate Audit"
              )}
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <label className="text-sm text-muted-foreground">Pages to audit:</label>
            <Input
              type="number"
              min={1}
              value={pageCount}
              onChange={(e) => setPageCount(Math.max(1, parseInt(e.target.value) || 1))}
              className="h-9 w-20 text-center text-sm border-2 focus:border-primary"
              disabled={isLoading}
              data-testid="page-count-input"
            />
            <span className="text-xs text-muted-foreground">(homepage + most important pages)</span>
          </div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <label className="text-sm text-muted-foreground">Spell checker:</label>
            <select
              value={spellcheckEngine}
              onChange={(e) => setSpellcheckEngine(e.target.value)}
              className="h-9 px-3 text-sm border-2 rounded-md focus:border-primary bg-background"
              disabled={isLoading}
              data-testid="spellcheck-engine-select"
            >
              {SPELLCHECK_ENGINES.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>

          <div className="mt-4 text-left">
            <label className="text-sm text-muted-foreground block mb-1">
              Context <span className="text-xs">(optional — client notes, call observations, known issues)</span>
            </label>
            <textarea
              value={manualContext}
              onChange={(e) => setManualContext(e.target.value)}
              placeholder={"Paste anything useful here — client emails, notes from a previous call, issues you've already noticed manually. The AI will treat this as confirmed signal and factor it into the report.\n\nExample: 'Client mentioned they get lots of calls asking what they do — their homepage isn't clear. Also noticed their testimonials section is empty.'"}
              className="w-full h-36 px-4 py-3 text-sm border-2 rounded-md focus:border-primary bg-background resize-y leading-relaxed"
              disabled={isLoading}
              data-testid="manual-context-input"
            />
          </div>
        </form>

        <p className="mt-6 text-sm text-muted-foreground animate-fade-in-up delay-300">
          Free audit includes SEO analysis, mobile check, and actionable recommendations
        </p>
      </div>
    </div>
  );
};

const LoadingScreen = ({ progress, message }) => {
  const steps = [
    { threshold: 10, label: "Fetching homepage", icon: Globe },
    { threshold: 15, label: "Discovering important pages", icon: Search },
    { threshold: 25, label: "Capturing screenshots", icon: Monitor },
    { threshold: 60, label: "Crawling & analyzing pages", icon: Globe },
    { threshold: 85, label: "Analyzing with AI", icon: Search },
    { threshold: 100, label: "Generating report", icon: FileText },
  ];

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 pt-24 pb-12" data-testid="loading-screen">
      <div className="w-full max-w-md text-center">
        <div className="loading-spinner mx-auto mb-8"></div>
        
        <h2 className="font-heading text-2xl font-semibold mb-2">Analyzing Website</h2>
        <p className="text-muted-foreground mb-8">{message}</p>
        
        <Progress value={progress} className="h-2 mb-8" />
        
        <div className="space-y-3 text-left">
          {steps.map((step, idx) => {
            const isActive = progress >= step.threshold - 20 && progress < step.threshold + 10;
            const isComplete = progress >= step.threshold;
            const Icon = step.icon;
            
            return (
              <div 
                key={idx}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  isActive ? 'bg-primary/5' : ''
                } ${isComplete ? 'text-success' : 'text-muted-foreground'}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'animate-pulse-slow' : ''}`} />
                <span className={isComplete ? 'line-through opacity-60' : ''}>
                  {step.label}
                </span>
                {isComplete && <CheckCircle2 className="w-4 h-4 ml-auto" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AuditScoreCard = ({ score, grade, url, platform }) => {
  const getGradeColor = (g) => {
    switch (g) {
      case 'A': return 'grade-a';
      case 'B': return 'grade-b';
      case 'C': return 'grade-c';
      case 'D': return 'grade-d';
      default: return 'grade-f';
    }
  };

  const getScoreColor = (s) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const isVibeCoded = platform && (
    platform.toLowerCase().includes('ai-generated') || 
    platform.toLowerCase().includes('lovable') ||
    platform.toLowerCase().includes('emergent') ||
    platform.toLowerCase().includes('bolt') ||
    platform.toLowerCase().includes('replit') ||
    platform.toLowerCase().includes('v0')
  );

  return (
    <Card className="overflow-hidden" data-testid="audit-score-card">
      <CardContent className="p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div 
            className="score-circle"
            style={{
              background: `conic-gradient(${getScoreColor(score)} ${score}%, #e2e8f0 0)`
            }}
          >
            <div className="score-circle-inner">
              <span className="font-heading text-4xl font-bold">{score}</span>
              <span className="block text-sm text-muted-foreground">out of 100</span>
            </div>
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-white font-heading font-bold text-xl ${getGradeColor(grade)}`}>
                {grade}
              </span>
              <h2 className="font-heading text-2xl font-semibold">Overall Score</h2>
            </div>
            <p className="text-muted-foreground mb-2">
              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-primary inline-flex items-center gap-1">
                {url} <ExternalLink className="w-3 h-3" />
              </a>
            </p>
            {platform && (
              <p className="text-sm mb-2">
                {isVibeCoded ? (
                  <Badge variant="destructive" className="bg-red-600">
                    ⚠️ Vibe-Coded: {platform}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    Built on {platform}
                  </Badge>
                )}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {score >= 80 && "Your site is performing well, but there's always room to optimize."}
              {score >= 60 && score < 80 && "Your site has a solid foundation but is likely losing customers to competitors with stronger web presence."}
              {score < 60 && "Critical issues detected. Your website may be actively hurting your business and driving customers to competitors."}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ScreenshotPreview = ({ desktopScreenshot, mobileScreenshot, aboveFoldScreenshot, mobileAboveFoldScreenshot, headerScreenshot }) => {
  const [activeTab, setActiveTab] = useState("above_fold");

  return (
    <Card data-testid="screenshot-preview">
      <CardHeader>
        <CardTitle className="font-heading text-xl">Visual Evidence</CardTitle>
        <CardDescription>Screenshots captured during the audit</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="above_fold" className="gap-2">
              <Monitor className="w-4 h-4" /> First Impression
            </TabsTrigger>
            <TabsTrigger value="mobile_fold" className="gap-2">
              <Smartphone className="w-4 h-4" /> Mobile First View
            </TabsTrigger>
            <TabsTrigger value="desktop" className="gap-2">
              <Monitor className="w-4 h-4" /> Full Desktop
            </TabsTrigger>
            <TabsTrigger value="mobile" className="gap-2">
              <Smartphone className="w-4 h-4" /> Full Mobile
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="above_fold">
            <div className="browser-frame">
              <div className="browser-frame-header">
                <div className="browser-dot red"></div>
                <div className="browser-dot yellow"></div>
                <div className="browser-dot green"></div>
                <span className="ml-4 text-xs text-muted-foreground">What visitors see first (above the fold)</span>
              </div>
              <div className="screenshot-container">
                {aboveFoldScreenshot ? (
                  <img 
                    src={`data:image/jpeg;base64,${aboveFoldScreenshot}`} 
                    alt="Above the fold screenshot"
                    className="w-full"
                  />
                ) : desktopScreenshot ? (
                  <img 
                    src={`data:image/jpeg;base64,${desktopScreenshot}`} 
                    alt="Desktop screenshot"
                    className="w-full"
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center bg-muted">
                    <p className="text-muted-foreground">Screenshot unavailable</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="mobile_fold">
            <div className="flex justify-center">
              <div className="browser-frame max-w-[280px]">
                <div className="browser-frame-header">
                  <div className="browser-dot red"></div>
                  <div className="browser-dot yellow"></div>
                  <div className="browser-dot green"></div>
                </div>
                <div className="screenshot-container max-h-[500px]">
                  {mobileAboveFoldScreenshot ? (
                    <img 
                      src={`data:image/jpeg;base64,${mobileAboveFoldScreenshot}`} 
                      alt="Mobile above fold screenshot"
                      className="w-full"
                    />
                  ) : mobileScreenshot ? (
                    <img 
                      src={`data:image/jpeg;base64,${mobileScreenshot}`} 
                      alt="Mobile screenshot"
                      className="w-full"
                    />
                  ) : (
                    <div className="h-64 flex items-center justify-center bg-muted">
                      <p className="text-muted-foreground">Screenshot unavailable</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="desktop">
            <div className="browser-frame">
              <div className="browser-frame-header">
                <div className="browser-dot red"></div>
                <div className="browser-dot yellow"></div>
                <div className="browser-dot green"></div>
              </div>
              <div className="screenshot-container">
                {desktopScreenshot ? (
                  <img 
                    src={`data:image/jpeg;base64,${desktopScreenshot}`} 
                    alt="Desktop screenshot"
                    className="w-full"
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center bg-muted">
                    <p className="text-muted-foreground">Screenshot unavailable</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="mobile">
            <div className="flex justify-center">
              <div className="browser-frame max-w-[280px]">
                <div className="browser-frame-header">
                  <div className="browser-dot red"></div>
                  <div className="browser-dot yellow"></div>
                  <div className="browser-dot green"></div>
                </div>
                <div className="screenshot-container max-h-[500px]">
                  {mobileScreenshot ? (
                    <img 
                      src={`data:image/jpeg;base64,${mobileScreenshot}`} 
                      alt="Mobile screenshot"
                      className="w-full"
                    />
                  ) : (
                    <div className="h-64 flex items-center justify-center bg-muted">
                      <p className="text-muted-foreground">Screenshot unavailable</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const EvidenceDetail = ({ evidence, ignoredWords = [] }) => {
  const [dismissed, setDismissed] = useState(new Set());
  const ignoredSet = useMemo(() => new Set((ignoredWords || []).map(w => w.toLowerCase())), [ignoredWords]);

  if (!evidence) return null;

  // If evidence is an array of typo objects, render styled
  if (Array.isArray(evidence)) {
    const visible = evidence.filter((item, i) => {
      if (dismissed.has(i)) return false;
      if (item.typo && ignoredSet.has(item.typo.toLowerCase())) return false;
      return true;
    });
    if (visible.length === 0) return null;

    return (
      <div className="mt-2 space-y-1.5">
        {evidence.map((item, i) => {
          if (dismissed.has(i)) return null;
          if (item.typo && ignoredSet.has(item.typo.toLowerCase())) return null;
          if (item.typo && item.correction) {
            return (
              <div key={i} className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 rounded px-3 py-1.5">
                <span className="text-red-600 font-semibold line-through">{item.typo}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-green-700 font-semibold">{item.correction}</span>
                {item.context && (
                  <span className="text-muted-foreground truncate max-w-[200px]" title={item.context}>
                    in: "{item.context.substring(0, 50)}..."
                  </span>
                )}
                <button
                  onClick={() => setDismissed(prev => new Set([...prev, i]))}
                  className="ml-auto text-muted-foreground hover:text-foreground text-xs whitespace-nowrap"
                >
                  Not a typo
                </button>
              </div>
            );
          }
          return <div key={i} className="text-xs text-muted-foreground">{JSON.stringify(item)}</div>;
        })}
      </div>
    );
  }

  // If evidence is a string, render as before
  if (typeof evidence === 'string') {
    // Check if it looks like JSON array string
    try {
      const parsed = JSON.parse(evidence);
      if (Array.isArray(parsed)) {
        return <EvidenceDetail evidence={parsed} />;
      }
    } catch {
      // Not JSON, render as plain text
    }
    return (
      <div className="mt-2 p-2 bg-slate-100 rounded text-xs text-slate-700">
        {evidence}
      </div>
    );
  }

  return null;
};

const EvidenceScreenshot = ({ issue, evidenceKey }) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  if (!issue.evidence_screenshot) return null;

  const isMobile = (issue.evidence_label || '').toLowerCase().includes('mobile');
  const DeviceIcon = isMobile ? Smartphone : Monitor;
  const imgSrc = `data:image/jpeg;base64,${issue.evidence_screenshot}`;

  return (
    <div className="mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1.5 transition-colors"
        data-testid={`evidence-toggle-${evidenceKey}`}
      >
        <DeviceIcon className="w-3.5 h-3.5" />
        {expanded ? 'Hide' : 'View'} Visual Proof
      </button>
      {expanded && (
        <div className="mt-2 rounded-lg border overflow-hidden shadow-sm">
          <div className="bg-slate-100 px-3 py-1.5 text-xs text-slate-600 flex items-center gap-2 border-b">
            <DeviceIcon className="w-3 h-3" />
            {issue.evidence_label || 'Screenshot evidence'}
          </div>
          <div
            className={`cursor-pointer ${isMobile ? "flex justify-center bg-slate-50 p-4" : ""}`}
            onClick={() => setModalOpen(true)}
          >
            <img
              src={imgSrc}
              alt={`Evidence: ${issue.title}`}
              className={isMobile ? "max-w-[375px] w-full border shadow-sm" : "w-full"}
            />
          </div>
        </div>
      )}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto p-2">
          <DialogHeader className="px-4 pt-3">
            <DialogTitle className="text-sm">{issue.evidence_label || 'Screenshot evidence'}</DialogTitle>
          </DialogHeader>
          <img
            src={imgSrc}
            alt={`Evidence: ${issue.title}`}
            className="w-full rounded"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const IssueList = ({ issues, ignoredWords = [] }) => {
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-error" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-warning" />;
      default:
        return <CheckCircle2 className="w-5 h-5 text-success" />;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'warning':
        return <Badge className="bg-warning text-white">Warning</Badge>;
      default:
        return <Badge className="bg-success text-white">Passed</Badge>;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'design': 'Design',
      'architecture': 'Architecture',
      'conversion': 'Conversion',
      'mobile': 'Mobile',
      'brand': 'Brand',
      'technical': 'Technical',
      'seo': 'SEO',
      'usability': 'Usability',
      'performance': 'Performance',
      'offer_clarity': 'Offer Clarity',
      'trust': 'Trust & Credibility',
      'navigation': 'Navigation',
      'content': 'Content',
      'platform': 'Platform',
    };
    return labels[category] || category;
  };

  const criticalIssues = issues.filter(i => i.severity === 'critical');
  const warningIssues = issues.filter(i => i.severity === 'warning');
  const passedIssues = issues.filter(i => i.severity === 'passed');

  const topIssues = [...criticalIssues, ...warningIssues].slice(0, 5);

  return (
    <Card data-testid="issue-list">
      <CardHeader>
        <CardTitle className="font-heading text-xl">Issues Costing You Customers</CardTitle>
        <CardDescription>
          <span className="text-error font-semibold">{criticalIssues.length} critical</span> issues requiring immediate attention, {warningIssues.length} warnings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
            Priority Issues
          </h4>
          <div className="space-y-2">
            {topIssues.map((issue, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border issue-card severity-${issue.severity}`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(issue.severity)}
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className="font-semibold">{issue.title}</span>
                      {getSeverityBadge(issue.severity)}
                      <Badge variant="outline" className="text-xs">{getCategoryLabel(issue.category)}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{issue.description}</p>
                    <EvidenceDetail evidence={issue.evidence} ignoredWords={ignoredWords} />
                    <EvidenceScreenshot issue={issue} evidenceKey={`top-${idx}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="all-issues">
            <AccordionTrigger className="text-sm">
              View all {issues.length} checks
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 pt-2">
                {issues.map((issue, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border issue-card severity-${issue.severity}`}
                  >
                    <div className="flex items-center gap-2">
                      {getSeverityIcon(issue.severity)}
                      <span className="font-medium flex-1">{issue.title}</span>
                      <Badge variant="outline" className="text-xs">{issue.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 ml-7">{issue.description}</p>
                    {issue.evidence_screenshot && (
                      <div className="ml-7">
                        <EvidenceScreenshot issue={issue} evidenceKey={`all-${idx}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const DetailedObservations = ({ observations, recommendations }) => {
  return (
    <Card data-testid="detailed-observations">
      <CardHeader>
        <CardTitle className="font-heading text-xl">The Full Picture</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
            What This Means For Your Business
          </h4>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{observations}</p>
        </div>
        
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
            Our Recommendation
          </h4>
          <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm">{recommendations}</pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ColdEmailGenerator = ({ email }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    toast.success("Email copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card data-testid="cold-email-generator">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" /> Outreach Email
            </CardTitle>
            <CardDescription>Ready-to-use cold email based on audit findings</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleCopy}
            data-testid="copy-email-btn"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" /> Copy Email
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="bg-slate-50 p-6 rounded-lg border">
          <pre className="email-template font-mono text-sm text-foreground whitespace-pre-wrap">
            {email}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
};

const LighthouseScores = ({ lighthouse, metrics }) => {
  if (!lighthouse) return null;

  const categories = [
    { key: 'performance', label: 'Perf' },
    { key: 'accessibility', label: 'A11y' },
    { key: 'bestPractices', label: 'BP' },
    { key: 'seo', label: 'SEO' },
  ];

  const getColor = (s) => {
    if (s == null) return 'bg-gray-200 text-gray-500';
    if (s >= 90) return 'bg-green-100 text-green-700';
    if (s >= 50) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="mt-3 mb-3">
      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
        Lighthouse Scores
      </div>
      <div className="flex gap-2 flex-wrap">
        {categories.map(({ key, label }) => {
          const val = lighthouse[key];
          return (
            <div key={key} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${getColor(val)}`}>
              {label}: {val != null ? val : '—'}
            </div>
          );
        })}
      </div>
      {metrics && (metrics.largestContentfulPaint || metrics.totalBlockingTime) && (
        <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
          {metrics.firstContentfulPaint && <span>FCP: {(metrics.firstContentfulPaint / 1000).toFixed(1)}s</span>}
          {metrics.largestContentfulPaint && <span>LCP: {(metrics.largestContentfulPaint / 1000).toFixed(1)}s</span>}
          {metrics.totalBlockingTime != null && <span>TBT: {Math.round(metrics.totalBlockingTime)}ms</span>}
          {metrics.cumulativeLayoutShift != null && <span>CLS: {metrics.cumulativeLayoutShift.toFixed(3)}</span>}
        </div>
      )}
    </div>
  );
};

const PageLevelResults = ({ pages }) => {
  const [expandedPage, setExpandedPage] = useState(null);

  if (!pages || pages.length === 0) return null;

  const getScoreColor = (s) => {
    if (s >= 80) return 'text-green-600';
    if (s >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-4 h-4 text-error" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-warning" />;
      default:
        return <CheckCircle2 className="w-4 h-4 text-success" />;
    }
  };

  return (
    <Card data-testid="page-level-results">
      <CardHeader>
        <CardTitle className="font-heading text-xl">Page-by-Page Results</CardTitle>
        <CardDescription>{pages.length} pages analyzed</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pages.map((page, idx) => {
            const isExpanded = expandedPage === idx;
            const pagePath = (() => {
              try { return new URL(page.url).pathname || '/'; }
              catch { return page.url; }
            })();
            const criticalCount = (page.issues || []).filter(i => i.severity === 'critical').length;
            const warningCount = (page.issues || []).filter(i => i.severity === 'warning').length;
            const lh = page.lighthouse;

            return (
              <div key={idx} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedPage(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{page.title || pagePath}</span>
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded text-white font-bold text-xs ${
                        page.score >= 80 ? 'bg-green-500' : page.score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {page.grade || '?'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="truncate">{pagePath}</span>
                      <span className={getScoreColor(page.score)}>{page.score}/100</span>
                      {criticalCount > 0 && <span className="text-red-600">{criticalCount} critical</span>}
                      {warningCount > 0 && <span className="text-amber-600">{warningCount} warnings</span>}
                      {lh && (
                        <span className="flex gap-1.5">
                          {lh.performance != null && <span className={`font-medium ${lh.performance >= 90 ? 'text-green-600' : lh.performance >= 50 ? 'text-amber-600' : 'text-red-600'}`}>P:{lh.performance}</span>}
                          {lh.accessibility != null && <span className={`font-medium ${lh.accessibility >= 90 ? 'text-green-600' : lh.accessibility >= 50 ? 'text-amber-600' : 'text-red-600'}`}>A:{lh.accessibility}</span>}
                          {lh.seo != null && <span className={`font-medium ${lh.seo >= 90 ? 'text-green-600' : lh.seo >= 50 ? 'text-amber-600' : 'text-red-600'}`}>S:{lh.seo}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                  <FileText className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-muted/20">
                    <LighthouseScores lighthouse={page.lighthouse} metrics={page.lighthouse_metrics} />
                    {page.observations && (
                      <p className="text-sm text-muted-foreground mt-3 mb-3">{page.observations}</p>
                    )}
                    <div className="space-y-2">
                      {(page.issues || []).map((issue, iIdx) => (
                        <div key={iIdx} className={`p-3 rounded-lg border issue-card severity-${issue.severity}`}>
                          <div className="flex items-center gap-2">
                            {getSeverityIcon(issue.severity)}
                            <span className="font-medium flex-1 text-sm">{issue.title}</span>
                            <Badge variant="outline" className="text-xs">{issue.category}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 ml-6">{issue.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const SPELLCHECK_ENGINE_LABELS = {
  pyspellchecker: 'PySpellChecker',
  hunspell: 'Hunspell',
  languagetool: 'LanguageTool',
  nspell: 'NSpell (Hunspell.js)',
  typojs: 'Typo.js',
};

const SpellingReview = ({ allTypos, ignoredWords, onIgnoreWord, onRestoreWord, spellcheckEngine, auditId, onRecheckComplete }) => {
  const [expandedPage, setExpandedPage] = useState(null);
  const [recheckEngine, setRecheckEngine] = useState(spellcheckEngine || 'languagetool');
  const [isRechecking, setIsRechecking] = useState(false);

  const handleRecheck = async () => {
    if (!auditId || isRechecking) return;
    setIsRechecking(true);
    try {
      const res = await axios.post(`${API}/audit/${auditId}/recheck-spelling`, { engine: recheckEngine });
      if (onRecheckComplete) {
        onRecheckComplete(res.data);
      }
      toast.success(`Spelling re-checked with ${SPELLCHECK_ENGINE_LABELS[recheckEngine] || recheckEngine}`);
    } catch (e) {
      toast.error('Re-check failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIsRechecking(false);
    }
  };

  if (!allTypos || allTypos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Spelling Review
          </CardTitle>
          <CardDescription>
            No misspellings found
            {spellcheckEngine && (
              <span className="ml-1">
                (checked with <span className="font-medium">{SPELLCHECK_ENGINE_LABELS[spellcheckEngine] || spellcheckEngine}</span>)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <label className="text-sm text-muted-foreground">Re-check with:</label>
            <select
              value={recheckEngine}
              onChange={(e) => setRecheckEngine(e.target.value)}
              className="px-3 py-1.5 border rounded-md text-sm bg-background"
              disabled={isRechecking}
            >
              {SPELLCHECK_ENGINES.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            <Button onClick={handleRecheck} disabled={isRechecking} size="sm" variant="outline">
              {isRechecking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Re-check Spelling
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group typos by page
  const typosByPage = {};
  allTypos.forEach((typo) => {
    const pageKey = typo.page_url || 'unknown';
    if (!typosByPage[pageKey]) {
      typosByPage[pageKey] = { title: typo.page_title || pageKey, typos: [] };
    }
    typosByPage[pageKey].typos.push(typo);
  });

  const ignoredSet = new Set((ignoredWords || []).map(w => w.toLowerCase()));
  const activeTypos = allTypos.filter(t => !ignoredSet.has(t.typo.toLowerCase()));
  const ignoredTypos = allTypos.filter(t => ignoredSet.has(t.typo.toLowerCase()));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> Spelling Review
            </CardTitle>
            <CardDescription>
              {activeTypos.length} potential misspelling{activeTypos.length !== 1 ? 's' : ''} found across all pages
              {spellcheckEngine && (
                <span className="ml-1">
                  using <span className="font-medium">{SPELLCHECK_ENGINE_LABELS[spellcheckEngine] || spellcheckEngine}</span>
                </span>
              )}
              {ignoredTypos.length > 0 && (
                <span className="ml-2 text-muted-foreground">({ignoredTypos.length} ignored)</span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={recheckEngine}
              onChange={(e) => setRecheckEngine(e.target.value)}
              className="px-2 py-1.5 border rounded-md text-xs bg-background"
              disabled={isRechecking}
            >
              {SPELLCHECK_ENGINES.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            <Button onClick={handleRecheck} disabled={isRechecking} size="sm" variant="outline">
              {isRechecking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Re-check
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {Object.entries(typosByPage).map(([pageUrl, pageData]) => {
          const pageActive = pageData.typos.filter(t => !ignoredSet.has(t.typo.toLowerCase()));
          const pageIgnored = pageData.typos.filter(t => ignoredSet.has(t.typo.toLowerCase()));
          const isExpanded = expandedPage === pageUrl;
          const pagePath = (() => {
            try { return new URL(pageUrl).pathname || '/'; }
            catch { return pageUrl; }
          })();

          return (
            <div key={pageUrl} className="mb-3 border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedPage(isExpanded ? null : pageUrl)}
                className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm truncate block">{pageData.title || pagePath}</span>
                  <span className="text-xs text-muted-foreground">{pagePath}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {pageActive.length > 0 && (
                    <span className="text-amber-600 font-medium">{pageActive.length} active</span>
                  )}
                  {pageIgnored.length > 0 && (
                    <span className="text-muted-foreground">{pageIgnored.length} ignored</span>
                  )}
                </div>
                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 border-t bg-muted/20 space-y-2 pt-2">
                  {pageData.typos.map((typo, tIdx) => {
                    const isIgnored = ignoredSet.has(typo.typo.toLowerCase());
                    return (
                      <div
                        key={tIdx}
                        className={`flex items-start gap-2 text-xs rounded px-3 py-2 border ${
                          isIgnored
                            ? 'bg-slate-50 border-slate-200 opacity-60'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${isIgnored ? 'text-slate-400' : 'text-red-600 line-through'}`}>
                              {typo.typo}
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <span className={`font-semibold ${isIgnored ? 'text-slate-400' : 'text-green-700'}`}>
                              {typo.correction}
                            </span>
                          </div>
                          {typo.context && (
                            <p className="text-muted-foreground truncate" title={typo.context}>
                              "{typo.context}"
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => isIgnored ? onRestoreWord(typo.typo) : onIgnoreWord(typo.typo)}
                          className={`shrink-0 px-2 py-1 rounded text-xs border whitespace-nowrap ${
                            isIgnored
                              ? 'border-blue-200 text-blue-600 hover:bg-blue-50'
                              : 'border-slate-200 text-muted-foreground hover:bg-slate-100'
                          }`}
                        >
                          {isIgnored ? (
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Restore</span>
                          ) : (
                            <span className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Not a typo</span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const SpellCheckTool = ({ ignoredWords = [] }) => {
  const [text, setText] = useState('');
  const [engine, setEngine] = useState('all');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [engines] = useState(['all', 'languagetool', 'pyspellchecker', 'hunspell', 'nspell', 'typojs']);

  const handleCheck = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/spellcheck`, {
        text: text.trim(),
        engine,
        ignored_words: ignoredWords
      });
      setResults(res.data);
    } catch (e) {
      toast.error('Spell check failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const engineLabels = {
    all: 'All Engines',
    pyspellchecker: 'PySpellChecker',
    hunspell: 'Hunspell',
    languagetool: 'LanguageTool',
    nspell: 'NSpell (Hunspell.js)',
    typojs: 'Typo.js'
  };

  const renderResults = () => {
    if (!results) return null;

    if (results.engine === 'all') {
      return (
        <div className="space-y-3 mt-4">
          {Object.entries(results.results).map(([eng, items]) => (
            <div key={eng} className="border rounded-lg p-3">
              <h4 className="text-sm font-medium mb-2">{engineLabels[eng] || eng}</h4>
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">No misspellings found</p>
              ) : (
                <div className="space-y-1">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-red-600 font-semibold line-through">{item.word}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-green-700 font-semibold">{item.correction}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-4 border rounded-lg p-3">
        <h4 className="text-sm font-medium mb-2">{engineLabels[results.engine] || results.engine}</h4>
        {results.results.length === 0 ? (
          <p className="text-xs text-muted-foreground">No misspellings found</p>
        ) : (
          <div className="space-y-1">
            {results.results.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-red-600 font-semibold line-through">{item.word}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-green-700 font-semibold">{item.correction}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Search className="w-4 h-4" /> Spell Check Tool
        </CardTitle>
        <CardDescription>Test spell checking with different engines</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste or type text to check for spelling errors..."
            className="w-full h-24 px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <div className="flex items-center gap-3">
            <select
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              {engines.map(e => (
                <option key={e} value={e}>{engineLabels[e] || e}</option>
              ))}
            </select>
            <Button onClick={handleCheck} disabled={loading || !text.trim()} size="sm">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Check Spelling
            </Button>
          </div>
          {renderResults()}
        </div>
      </CardContent>
    </Card>
  );
};

const AuditReport = ({ audit, onNewAudit, onDownloadPDF, onAuditUpdate }) => {
  const [pdfExportType, setPdfExportType] = useState(null);
  const [ignoredWords, setIgnoredWords] = useState(audit.ignored_words || []);
  const hasMultiplePages = audit.pages_audited && audit.pages_audited.length > 1;

  const handlePdfDownload = (exportType) => {
    setPdfExportType(null);
    onDownloadPDF(exportType);
  };

  const saveIgnoredWords = useCallback(async (words) => {
    try {
      await axios.patch(`${API}/audit/${audit.id}/ignored-words`, { ignored_words: words });
    } catch (e) {
      console.error('Failed to save ignored words:', e);
    }
  }, [audit.id]);

  const handleIgnoreWord = useCallback((word) => {
    setIgnoredWords(prev => {
      const next = [...prev, word.toLowerCase()];
      saveIgnoredWords(next);
      return next;
    });
  }, [saveIgnoredWords]);

  const handleRestoreWord = useCallback((word) => {
    setIgnoredWords(prev => {
      const next = prev.filter(w => w !== word.toLowerCase());
      saveIgnoredWords(next);
      return next;
    });
  }, [saveIgnoredWords]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="audit-report">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Audit Report</h1>
          {hasMultiplePages && (
            <p className="text-sm text-muted-foreground mt-1">
              {audit.pages_audited.length} pages analyzed
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onNewAudit} data-testid="new-audit-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> New Audit
          </Button>
          <div className="relative">
            <Button
              onClick={() => setPdfExportType(pdfExportType ? null : 'menu')}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            {pdfExportType === 'menu' && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => handlePdfDownload('summary')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50"
                >
                  Summary Only
                </button>
                <button
                  onClick={() => handlePdfDownload('detailed')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50"
                >
                  Detailed (Page-Level)
                </button>
                <button
                  onClick={() => handlePdfDownload('full')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50"
                >
                  Full Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6" id="audit-report-content">
        <AuditScoreCard
          score={audit.score}
          grade={audit.grade}
          url={audit.url}
          platform={audit.platform}
        />

        {audit.pages_audited && audit.pages_audited.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Globe className="w-4 h-4" /> Pages Scanned ({audit.pages_audited.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {audit.pages_audited.map((page, idx) => {
                  const pagePath = (() => {
                    try { return new URL(page.url).pathname || '/'; }
                    catch { return page.url; }
                  })();
                  return (
                    <a
                      key={idx}
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs hover:bg-muted/50 transition-colors"
                    >
                      <span className={`w-5 h-5 rounded text-white font-bold text-[10px] flex items-center justify-center ${
                        page.score >= 80 ? 'bg-green-500' : page.score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {page.grade || '?'}
                      </span>
                      <span className="font-medium truncate max-w-[200px]">{pagePath}</span>
                      <ExternalLink className="w-3 h-3 text-muted-foreground" />
                    </a>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ScreenshotPreview
            desktopScreenshot={audit.desktop_screenshot}
            mobileScreenshot={audit.mobile_screenshot}
            aboveFoldScreenshot={audit.above_fold_screenshot}
            mobileAboveFoldScreenshot={audit.mobile_above_fold_screenshot}
            headerScreenshot={audit.header_screenshot}
          />
          <IssueList issues={audit.issues} ignoredWords={ignoredWords} />
        </div>

        <DetailedObservations
          observations={audit.detailed_observations}
          recommendations={audit.recommended_fixes}
        />

        {hasMultiplePages && (
          <PageLevelResults pages={audit.pages_audited} />
        )}

        <SpellingReview
          allTypos={audit.all_typos || []}
          ignoredWords={ignoredWords}
          onIgnoreWord={handleIgnoreWord}
          onRestoreWord={handleRestoreWord}
          spellcheckEngine={audit.spellcheck_engine || 'languagetool'}
          auditId={audit.id}
          onRecheckComplete={(data) => {
            onAuditUpdate({
              ...audit,
              all_typos: data.all_typos,
              spellcheck_engine: data.engine
            });
          }}
        />

        <SpellCheckTool ignoredWords={ignoredWords} />

        <ColdEmailGenerator email={audit.cold_email} />
      </div>
    </div>
  );
};

// ========== Main App ==========

function App() {
  const [audit, setAudit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  // Check URL for audit ID on mount
  const auditIdFromUrl = useMemo(() => {
    const match = window.location.pathname.match(/^\/audit\/([a-f0-9-]+)$/);
    return match ? match[1] : null;
  }, []);

  useEffect(() => {
    if (auditIdFromUrl) {
      // Load existing audit from URL
      setIsLoading(true);
      setProgressMessage("Loading audit report...");
      axios.get(`${API}/audit/${auditIdFromUrl}`)
        .then((response) => {
          const data = response.data;
          if (data.status === "completed") {
            setAudit(data);
            setIsLoading(false);
          } else if (data.status === "error") {
            setIsLoading(false);
            toast.error("This audit failed to complete.");
          } else {
            // Still in progress, poll
            pollAuditStatus(auditIdFromUrl);
          }
        })
        .catch(() => {
          setIsLoading(false);
          toast.error("Audit not found.");
          window.history.replaceState({}, '', '/');
        })
        .finally(() => setInitialLoad(false));
    } else {
      setInitialLoad(false);
    }
  }, [auditIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollAuditStatus = useCallback(async (auditId) => {
    try {
      const response = await axios.get(`${API}/audit/${auditId}`);
      const data = response.data;

      setProgress(data.progress);
      setProgressMessage(data.progress_message);

      if (data.status === "completed") {
        setAudit(data);
        setIsLoading(false);
        window.history.replaceState({}, '', `/audit/${auditId}`);
        toast.success("Audit complete!");
      } else if (data.status === "error") {
        setIsLoading(false);
        toast.error(data.progress_message || "Audit failed");
      } else {
        // Continue polling
        setTimeout(() => pollAuditStatus(auditId), 2000);
      }
    } catch (error) {
      console.error("Poll error:", error);
      setIsLoading(false);
      toast.error("Failed to get audit status");
    }
  }, []);

  const handleSubmit = async (url, pageCount = 5, spellcheckEngine = 'languagetool', manualContext = '') => {
    setIsLoading(true);
    setProgress(0);
    setProgressMessage("Starting audit...");
    setAudit(null);

    try {
      const response = await axios.post(`${API}/audit`, { url, page_count: pageCount, spellcheck_engine: spellcheckEngine, manual_context: manualContext });
      const auditId = response.data.id;

      // Start polling
      pollAuditStatus(auditId);
    } catch (error) {
      console.error("Submit error:", error);
      setIsLoading(false);
      toast.error("Failed to start audit. Please check the URL and try again.");
    }
  };

  const handleNewAudit = () => {
    setAudit(null);
    setProgress(0);
    setProgressMessage("");
    window.history.replaceState({}, '', '/');
  };

  const handleSelectAudit = async (auditId) => {
    setShowHistory(false);
    setIsLoading(true);
    setProgressMessage("Loading audit report...");
    try {
      const response = await axios.get(`${API}/audit/${auditId}`);
      const data = response.data;
      if (data.status === "completed") {
        setAudit(data);
        setIsLoading(false);
        window.history.replaceState({}, '', `/audit/${auditId}`);
      } else {
        setIsLoading(false);
        toast.error("This audit is not yet complete.");
      }
    } catch {
      setIsLoading(false);
      toast.error("Failed to load audit.");
    }
  };

  const handleDownloadPDF = async (exportType = 'full') => {
    toast.info("Generating PDF...");

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const W = pdf.internal.pageSize.getWidth();
      const H = pdf.internal.pageSize.getHeight();
      const hasPages = audit.pages_audited && audit.pages_audited.length > 1;

      // Brand colors
      const BRAND = [30, 19, 84];           // #1E1354 deep purple
      const BRAND_MID = [60, 45, 130];      // lighter purple for accents
      const BRAND_PALE = [200, 195, 240];   // pale purple for text on dark
      const BRAND_LIGHT_BG = [245, 243, 255]; // very light purple bg
      const DARK = [15, 23, 42];            // body text
      const MUTED = [100, 116, 139];        // secondary text
      const WHITE = [255, 255, 255];
      const GRAY_BORDER = [226, 232, 240];
      const SEV = {
        critical: [239, 68, 68],
        warning: [245, 158, 11],
        passed: [16, 185, 129],
      };
      const M = 20; // margin
      const CW = W - M * 2; // content width

      // Load logo images
      let whiteLogo = null;
      let purpleLogo = null;
      try {
        const loadImg = (src) => new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = () => resolve(null);
          img.src = src;
        });
        whiteLogo = await loadImg('/waypoint-logo-white.png');
        purpleLogo = await loadImg('/waypoint-logo-purple.png');
      } catch { /* logos optional */ }

      // Helpers
      const tc = (c) => pdf.setTextColor(c[0], c[1], c[2]);
      const fc = (c) => pdf.setFillColor(c[0], c[1], c[2]);
      const dc = (c) => pdf.setDrawColor(c[0], c[1], c[2]);
      const scoreColor = (s) => s >= 80 ? SEV.passed : s >= 60 ? SEV.warning : SEV.critical;
      let pageNum = 1;

      const addLogo = (logoData, x, y, w) => {
        if (logoData) {
          const props = pdf.getImageProperties(logoData);
          const h = (w / props.width) * props.height;
          pdf.addImage(logoData, 'PNG', x, y, w, h);
          return h;
        }
        return 0;
      };

      const footer = () => {
        // Purple logo in footer (small)
        if (purpleLogo) {
          addLogo(purpleLogo, M, H - 14, 22);
        } else {
          tc(MUTED); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
          pdf.text('waypoint.agency', M, H - 10);
        }
        tc(MUTED); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
        pdf.text('Page ' + pageNum, W - M, H - 10, { align: 'right' });
        dc(BRAND); pdf.setLineWidth(0.3);
        pdf.line(M, H - 16, W - M, H - 16);
      };

      const np = () => { pdf.addPage(); pageNum++; footer(); };

      const heading = (text, y) => {
        fc(BRAND); pdf.rect(M, y - 5, 3, 8, 'F');
        tc(BRAND); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
        pdf.text(text, M + 7, y);
        return y + 12;
      };

      const fitY = (y, need) => {
        if (y + need > H - 20) { np(); return 22; }
        return y;
      };

      // ═══════════════════════════════════════
      //  COVER PAGE
      // ═══════════════════════════════════════
      fc(BRAND); pdf.rect(0, 0, W, H, 'F');

      // Decorative stripe
      fc(BRAND_MID); pdf.rect(0, 0, 6, H, 'F');

      // Logo (top left)
      if (whiteLogo) {
        addLogo(whiteLogo, M, 28, 60);
      } else {
        tc(WHITE); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
        pdf.text('WAYPOINT', M, 35);
      }
      tc(BRAND_PALE); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
      pdf.text('waypoint.agency', M, 48);

      // Accent bar
      fc(BRAND_PALE); pdf.rect(M, 58, 50, 1.5, 'F');

      // Title
      tc(WHITE); pdf.setFontSize(36); pdf.setFont(undefined, 'bold');
      pdf.text('Website', M, 80);
      pdf.text('Audit Report', M, 96);

      // Divider
      fc(BRAND_PALE); pdf.rect(M, 104, 50, 0.5, 'F');

      // URL & metadata
      tc(BRAND_PALE); pdf.setFontSize(12); pdf.setFont(undefined, 'normal');
      pdf.text(audit.url, M, 118);
      let coverY = 128;
      if (hasPages) {
        pdf.setFontSize(10);
        pdf.text(audit.pages_audited.length + ' pages analyzed', M, coverY);
        coverY += 8;
      }
      pdf.setFontSize(9);
      pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), M, coverY);

      // Platform
      if (audit.platform) {
        tc(BRAND_PALE); pdf.setFontSize(9); pdf.setFont(undefined, 'normal');
        pdf.text('Built with: ' + audit.platform, M, H - 40);
      }

      // Footer tagline
      tc(BRAND_PALE); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
      pdf.text('Confidential \u2014 Prepared by Waypoint Agency', M, H - 15);
      pdf.text(String(new Date().getFullYear()), W - M, H - 15, { align: 'right' });

      // ═══════════════════════════════════════
      //  SALES INTEL (internal use)
      // ═══════════════════════════════════════
      np();
      let siy = heading('Site Background — Internal Use Only', 25);

      // "INTERNAL" watermark-style label
      fc([239, 68, 68]); pdf.rect(W - M - 40, 17, 40, 8, 'F');
      tc(WHITE); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
      pdf.text('INTERNAL ONLY', W - M - 20, 22, { align: 'center' });

      // Company summary (AI-generated)
      if (audit.business_summary) {
        fc(BRAND_LIGHT_BG); pdf.rect(M, siy, CW, 28, 'F');
        fc(BRAND); pdf.rect(M, siy, CW, 1.5, 'F');
        siy += 7;
        tc(BRAND); pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
        pdf.text('What They Do', M + 5, siy);
        siy += 6;
        tc(DARK); pdf.setFontSize(9); pdf.setFont(undefined, 'normal');
        const bsLines = pdf.splitTextToSize(audit.business_summary, CW - 10);
        bsLines.slice(0, 4).forEach(l => { pdf.text(l, M + 5, siy); siy += 4; });
        siy += 6;
      }

      // Technical details box
      fc([248, 250, 252]); pdf.rect(M, siy, CW, 24, 'F');
      siy += 6;
      tc(MUTED); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
      pdf.text('Site Metadata', M + 5, siy);
      siy += 5;
      tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
      if (audit.page_title) {
        pdf.text('Title: ' + audit.page_title, M + 5, siy); siy += 4;
      }
      if (audit.meta_description) {
        const mdLines = pdf.splitTextToSize('Meta: ' + audit.meta_description, CW - 10);
        mdLines.slice(0, 2).forEach(l => { pdf.text(l, M + 5, siy); siy += 3.5; });
      }
      if (audit.h1_tag) {
        pdf.text('H1: ' + audit.h1_tag, M + 5, siy); siy += 4;
      }
      siy += 4;

      // Two-column layout for key facts
      const col1X = M;
      const col2X = M + CW / 2 + 5;
      const colW = CW / 2 - 5;

      // Platform & Tech column header
      fc(BRAND); pdf.rect(col1X, siy, colW, 7, 'F');
      tc(WHITE); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
      pdf.text('Platform & Technical', col1X + 3, siy + 5);

      fc(BRAND); pdf.rect(col2X, siy, colW, 7, 'F');
      tc(WHITE); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
      pdf.text('Contact & Trust Signals', col2X + 3, siy + 5);
      siy += 11;

      const intel = audit.site_intel || {};
      tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');

      // Left column - Platform & Tech
      let ly = siy;
      const addRow = (label, value, x, y) => {
        tc(MUTED); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
        pdf.text(label, x + 3, y);
        tc(DARK); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
        pdf.text(String(value), x + 3, y + 4);
        return y + 10;
      };
      const addCheck = (label, checked, x, y) => {
        fc(checked ? [16, 185, 129] : [239, 68, 68]);
        pdf.rect(x + 3, y - 2, 3, 3, 'F');
        tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
        pdf.text(label, x + 9, y);
        return y + 6;
      };

      ly = addRow('Platform', audit.platform || 'Unknown', col1X, ly);
      if (intel.is_template_platform) {
        tc([239, 68, 68]); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
        pdf.text('DIY / Template Builder', col1X + 3, ly - 4);
        ly += 2;
      }
      if (intel.is_vibe_coded) {
        tc([239, 68, 68]); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
        pdf.text('Likely AI-generated / Vibe-coded', col1X + 3, ly - 4);
        ly += 2;
      }
      ly = addCheck('HTTPS', intel.is_https !== false, col1X, ly);
      ly = addCheck('Schema/Structured Data', intel.has_schema, col1X, ly);
      ly = addCheck('Open Graph Tags', intel.has_og_tags, col1X, ly);
      ly = addCheck('Privacy Policy', intel.has_privacy_policy, col1X, ly);
      ly = addCheck('Favicon', intel.has_favicon, col1X, ly);
      ly = addRow('Word Count (homepage)', intel.word_count || 'N/A', col1X, ly);
      ly = addRow('Total Images', (intel.total_images || 0) + ' (' + (intel.images_with_alt || 0) + ' with alt text)', col1X, ly);

      // Right column - Contact & Trust
      let ry = siy;
      ry = addCheck('Phone Number Visible', intel.has_phone, col2X, ry);
      if (intel.phone_numbers && intel.phone_numbers.length > 0) {
        tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
        pdf.text(intel.phone_numbers.join(', '), col2X + 9, ry - 2);
        ry += 4;
      }
      ry = addCheck('Email Visible', intel.has_email_visible, col2X, ry);
      if (intel.emails_found && intel.emails_found.length > 0) {
        tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
        pdf.text(intel.emails_found.join(', '), col2X + 9, ry - 2);
        ry += 4;
      }
      ry = addCheck('Physical Address', intel.has_address, col2X, ry);
      ry = addCheck('Social Proof / Testimonials', intel.has_social_proof, col2X, ry);
      ry = addCheck('Trust Badges', intel.has_trust_badges, col2X, ry);
      ry = addCheck('Email Capture / Newsletter', intel.has_email_capture, col2X, ry);
      ry = addRow('CTAs Found', intel.cta_count || 0, col2X, ry);
      if (intel.cta_buttons && intel.cta_buttons.length > 0) {
        tc(MUTED); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
        const ctaText = intel.cta_buttons.slice(0, 5).join(' | ');
        const ctaLines = pdf.splitTextToSize(ctaText, colW - 10);
        ctaLines.slice(0, 2).forEach(l => { pdf.text(l, col2X + 3, ry); ry += 3.5; });
      }
      ry = addRow('Forms Found', intel.form_count || 0, col2X, ry);

      siy = Math.max(ly, ry) + 6;

      // Navigation structure
      if (intel.nav_links && intel.nav_links.length > 0) {
        siy = fitY(siy, 20);
        fc(BRAND_LIGHT_BG); pdf.rect(M, siy, CW, 18, 'F');
        fc(BRAND); pdf.rect(M, siy, CW, 1.5, 'F');
        siy += 7;
        tc(BRAND); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
        pdf.text('Site Navigation (' + intel.nav_links.length + ' links)', M + 5, siy);
        siy += 5;
        tc(DARK); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
        const navText = intel.nav_links.join('  \u2022  ');
        const navLines = pdf.splitTextToSize(navText, CW - 10);
        navLines.slice(0, 2).forEach(l => { pdf.text(l, M + 5, siy); siy += 3.5; });
        siy += 4;
      }

      // Platform limitations (if any)
      if (intel.platform_limitations) {
        siy = fitY(siy, 24);
        fc([254, 243, 199]); pdf.rect(M, siy, CW, 20, 'F'); // yellow bg
        fc([245, 158, 11]); pdf.rect(M, siy, 3, 20, 'F'); // amber left bar
        siy += 7;
        tc([120, 53, 15]); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
        pdf.text('Platform Limitations — Selling Opportunity', M + 7, siy);
        siy += 5;
        tc([120, 53, 15]); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
        const plLines = pdf.splitTextToSize(intel.platform_limitations, CW - 14);
        plLines.slice(0, 3).forEach(l => { pdf.text(l, M + 7, siy); siy += 3.5; });
      }

      // Lighthouse scores summary if available
      const homepage = (audit.pages_audited || [])[0];
      if (homepage && homepage.lighthouse) {
        siy = fitY(siy, 30);
        siy += 4;
        tc(BRAND); pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
        pdf.text('Lighthouse Scores', M, siy);
        siy += 8;
        const lhScores = [
          { label: 'Performance', val: homepage.lighthouse.performance },
          { label: 'Accessibility', val: homepage.lighthouse.accessibility },
          { label: 'SEO', val: homepage.lighthouse.seo },
        ].filter(s => s.val != null);

        lhScores.forEach((s, i) => {
          const bx = M + i * 58;
          const sc2 = scoreColor(s.val);
          fc(sc2); pdf.rect(bx, siy, 50, 14, 'F');
          tc(WHITE); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
          pdf.text(String(s.val), bx + 25, siy + 7, { align: 'center' });
          pdf.setFontSize(6); pdf.setFont(undefined, 'normal');
          pdf.text(s.label, bx + 25, siy + 12, { align: 'center' });
        });
      }

      // ═══════════════════════════════════════
      //  SUMMARY
      // ═══════════════════════════════════════
      if (exportType === 'summary' || exportType === 'full') {

        // Screenshot
        if (audit.desktop_screenshot) {
          np();
          let y = heading('Current Website', 25);

          // Browser bar
          fc([243, 244, 246]); pdf.rect(M, y, CW, 8, 'F');
          // Traffic dots (small squares as fallback)
          pdf.setFillColor(255, 95, 87); pdf.rect(M + 4, y + 2.5, 3, 3, 'F');
          pdf.setFillColor(254, 188, 46); pdf.rect(M + 10, y + 2.5, 3, 3, 'F');
          pdf.setFillColor(40, 200, 64); pdf.rect(M + 16, y + 2.5, 3, 3, 'F');
          tc(MUTED); pdf.setFontSize(5);
          pdf.text(audit.url, M + 24, y + 5.5);

          // Screenshot - preserve aspect ratio
          const img = 'data:image/jpeg;base64,' + audit.desktop_screenshot;
          const imgProps = pdf.getImageProperties(img);
          const imgH = (CW / imgProps.width) * imgProps.height;
          const maxH = H - y - 30; // leave room for footer
          const finalH = Math.min(imgH, maxH);
          pdf.addImage(img, 'JPEG', M, y + 8, CW, finalH);

          // Border
          dc(GRAY_BORDER); pdf.setLineWidth(0.3);
          pdf.rect(M, y, CW, finalH + 8);
        }

        // Issues Found
        np();
        let y = heading('Issues Found', 25);

        // Severity summary badges
        const counts = [
          { label: 'Critical', n: audit.issues.filter(i => i.severity === 'critical').length, c: SEV.critical },
          { label: 'Warnings', n: audit.issues.filter(i => i.severity === 'warning').length, c: SEV.warning },
          { label: 'Passed', n: audit.issues.filter(i => i.severity === 'passed').length, c: SEV.passed },
        ];
        counts.forEach((b, i) => {
          const bx = M + i * 58;
          fc(b.c); pdf.rect(bx, y, 50, 16, 'F');
          tc(WHITE); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
          pdf.text(String(b.n), bx + 25, y + 7, { align: 'center' });
          pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
          pdf.text(b.label, bx + 25, y + 13, { align: 'center' });
        });
        y += 26;

        // Issue cards
        audit.issues.slice(0, 15).forEach((issue) => {
          y = fitY(y, 22);

          const sevC = SEV[issue.severity] || MUTED;

          // Left severity bar
          fc(sevC); pdf.rect(M, y - 1, 2.5, 16, 'F');

          // Light background
          fc(BRAND_LIGHT_BG); pdf.rect(M + 2.5, y - 1, CW - 2.5, 16, 'F');

          // Title
          tc(DARK); pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
          const tLines = pdf.splitTextToSize(issue.title || '', CW - 14);
          pdf.text(tLines[0], M + 7, y + 4);

          // Severity label
          tc(sevC); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
          pdf.text(issue.severity.toUpperCase(), W - M - 2, y + 4, { align: 'right' });

          y += 7;

          // Description
          tc(MUTED); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
          const dLines = pdf.splitTextToSize(issue.description || '', CW - 14);
          dLines.slice(0, 2).forEach((line) => {
            pdf.text(line, M + 7, y + 1);
            y += 3.5;
          });

          y += 6;
        });

        // Recommendations
        if (audit.recommended_fixes) {
          np();
          let ry = heading('Recommendations', 25);

          // Light background block
          fc(BRAND_LIGHT_BG); pdf.rect(M, ry - 2, CW, H - ry - 20, 'F');
          // Purple top accent
          fc(BRAND); pdf.rect(M, ry - 2, CW, 1.5, 'F');
          ry += 6; // spacing below accent line

          tc(DARK); pdf.setFontSize(9); pdf.setFont(undefined, 'normal');
          const recLines = pdf.splitTextToSize(audit.recommended_fixes, CW - 10);
          recLines.forEach((line) => {
            ry = fitY(ry, 6);
            pdf.text(line, M + 5, ry + 2);
            ry += 4.5;
          });
        }
      }

      // ═══════════════════════════════════════
      //  PAGE-BY-PAGE RESULTS
      // ═══════════════════════════════════════
      if ((exportType === 'detailed' || exportType === 'full') && hasPages) {
        np();
        let y = heading('Page-by-Page Results', 25);

        audit.pages_audited.forEach((page, pIdx) => {
          y = fitY(y, 40);

          const pagePath = (() => {
            try { return new URL(page.url).pathname || '/'; } catch { return page.url; }
          })();
          const ps = page.score || 0;
          const pc = scoreColor(ps);

          // Page card header background
          fc(BRAND_LIGHT_BG); pdf.rect(M, y - 2, CW, 18, 'F');
          // Left accent
          fc(BRAND); pdf.rect(M, y - 2, 3, 18, 'F');

          // Title
          tc(BRAND); pdf.setFontSize(11); pdf.setFont(undefined, 'bold');
          const pageTitle = (pIdx + 1) + '. ' + (page.title || pagePath);
          const ptLines = pdf.splitTextToSize(pageTitle, CW - 35);
          pdf.text(ptLines[0], M + 7, y + 5);

          // Score badge
          fc(pc); pdf.rect(W - M - 22, y, 18, 12, 'F');
          tc(WHITE); pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
          pdf.text(String(ps), W - M - 13, y + 8, { align: 'center' });

          // URL
          tc(MUTED); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
          pdf.text(page.url, M + 7, y + 12);

          y += 22;

          // Observations
          if (page.observations) {
            tc(DARK); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
            const obsL = pdf.splitTextToSize(page.observations, CW - 12);
            obsL.slice(0, 3).forEach((line) => {
              y = fitY(y, 5);
              pdf.text(line, M + 7, y);
              y += 3.5;
            });
            y += 3;
          }

          // Page issues
          (page.issues || []).forEach((issue) => {
            y = fitY(y, 14);

            const sc2 = SEV[issue.severity] || MUTED;
            // Severity dot (small square)
            fc(sc2); pdf.rect(M + 5, y - 1.5, 2.5, 2.5, 'F');

            tc(DARK); pdf.setFontSize(8.5); pdf.setFont(undefined, 'bold');
            pdf.text(issue.title || '', M + 10, y);
            y += 4;

            if (issue.description) {
              tc(MUTED); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
              const dl = pdf.splitTextToSize(issue.description, CW - 16);
              dl.slice(0, 2).forEach((line) => {
                pdf.text(line, M + 10, y);
                y += 3;
              });
            }
            y += 3;
          });

          // Separator between pages
          dc(GRAY_BORDER); pdf.setLineWidth(0.2);
          pdf.line(M, y + 2, W - M, y + 2);
          y += 8;
        });
      }

      // ═══════════════════════════════════════
      //  CTA PAGE
      // ═══════════════════════════════════════
      np();

      // Purple bottom half
      fc(BRAND); pdf.rect(0, H * 0.55, W, H * 0.45, 'F');

      // CTA text (white area)
      tc(BRAND); pdf.setFontSize(24); pdf.setFont(undefined, 'bold');
      pdf.text('Ready to improve your', W / 2, 80, { align: 'center' });
      pdf.text('online presence?', W / 2, 94, { align: 'center' });

      tc(MUTED); pdf.setFontSize(11); pdf.setFont(undefined, 'normal');
      pdf.text("Let's discuss how Waypoint can help you achieve your goals.", W / 2, 112, { align: 'center' });

      // On purple section - logo + URL
      if (whiteLogo) {
        const logoW = 70;
        addLogo(whiteLogo, W / 2 - logoW / 2, H - 85, logoW);
      } else {
        tc(WHITE); pdf.setFontSize(20); pdf.setFont(undefined, 'bold');
        pdf.text('WAYPOINT', W / 2, H - 75, { align: 'center' });
      }
      tc(WHITE); pdf.setFontSize(14); pdf.setFont(undefined, 'normal');
      pdf.text('waypoint.agency', W / 2, H - 58, { align: 'center' });
      // Underline link
      dc(WHITE); pdf.setLineWidth(0.3);
      pdf.line(W / 2 - 22, H - 56, W / 2 + 22, H - 56);

      tc(BRAND_PALE); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
      pdf.text('Audit powered by Waypoint Agency', W / 2, H - 40, { align: 'center' });

      // Clickable link
      pdf.link(W / 2 - 30, H - 78, 60, 14, { url: 'https://waypoint.agency' });

      pdf.save('waypoint-audit-' + new URL(audit.url).hostname + '-' + Date.now() + '.pdf');
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  if (initialLoad) return null;

  return (
    <div className="App min-h-screen bg-background">
      <Toaster position="top-right" />

      {/* Global top-right: deployment badge is always visible; Past Audits
          only on the home screen. z-50 keeps them above page content but
          the history dialog / PDF menu can still overlay via their own
          higher z-indexes when needed. */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-3">
        <DeploymentBadge />
        {!audit && !isLoading && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(true)}
            data-testid="history-btn"
          >
            <History className="w-4 h-4 mr-2" /> Past Audits
          </Button>
        )}
      </div>

      {!audit && !isLoading && (
        <InputSection onSubmit={handleSubmit} isLoading={isLoading} />
      )}

      {isLoading && (
        <LoadingScreen progress={progress} message={progressMessage} />
      )}

      {audit && !isLoading && (
        <AuditReport
          audit={audit}
          onNewAudit={handleNewAudit}
          onDownloadPDF={handleDownloadPDF}
          onAuditUpdate={setAudit}
        />
      )}

      {showHistory && (
        <AuditHistory
          onSelectAudit={handleSelectAudit}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

export default App;
